import {
  UseInfiniteQueryResult,
  keepPreviousData,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { OrderByInput } from '../types';
import { cacheKeys } from './cacheKeys';
import { GraphbackOperationType, getDataObject } from './getDataObject';
import { useFetcher } from './useFetcher';

export type InfiniteFindByQueryOptions = {
  query: any;
  modelName: string;
  filter?: any;
  orderBy?: OrderByInput;
  pageSize: number;
  withoutCache?: boolean;
  dataPrefix?: string;
  /** Defaults to true. False withholds the request (e.g. until a persisted filter has loaded). */
  enabled?: boolean;
  /** Disable stale rows across query keys for scoped streams. */
  keepPreviousData?: boolean;
};

/**
 * The one react-query key an infinite find keeps all its pages under.
 * Exported so a writer — an optimistic insert that has to show a new row
 * before the server answers — can address the same cache entry the reader
 * created, instead of re-deriving this shape and silently missing it.
 *
 * pageSize is part of the key so two hooks over the same filter but different
 * page sizes do not collide (which would cross their pagination).
 */
export const infiniteFindByQueryCacheKey = (
  opts: Pick<
    InfiniteFindByQueryOptions,
    'modelName' | 'filter' | 'orderBy' | 'pageSize'
  >
) =>
  cacheKeys(opts.modelName).list({
    ...opts.filter,
    ...opts.orderBy,
    pageSize: opts.pageSize,
  });

export const useInfiniteFindByQuery = <TData, TError>(
  opts: InfiniteFindByQueryOptions
): UseInfiniteQueryResult<TData, TError> => {
  const fetcher = useFetcher<any, any>(opts.query);
  const cacheKey = infiniteFindByQueryCacheKey(opts);
  const cacheTimeDefault = 300000; // 5 minutes - default react-query cache time
  const cacheTime = opts.withoutCache ? 0 : cacheTimeDefault;

  const pageFetcher = async (args: any) => {
    const { offset } = args.pageParam || { offset: 0 };
    const results = await fetcher({
      filter: opts.filter,
      orderBy: opts.orderBy,
      page: {
        limit: opts.pageSize,
        offset: offset,
      },
    });

    const data = getDataObject(
      results,
      opts.modelName,
      GraphbackOperationType.find,
      opts.dataPrefix
    );

    return data;
  };

  return useInfiniteQuery({
    queryKey: cacheKey,
    queryFn: pageFetcher,
    enabled: opts.enabled ?? true,
    gcTime: cacheTime,
    // Keep the previous sort/filter's rows on screen while the new query loads.
    // orderBy/filter are part of the cache key, so without this every new sort
    // or filter is a cold cache miss that blanks the list (the "whole page
    // reloads on first sort" symptom on the mobile Assets list). With it, the
    // list stays put and swaps in place.
    placeholderData: opts.keepPreviousData === false ? undefined : keepPreviousData,
    initialPageParam: { offset: 0 },
    getNextPageParam: (lastPage: any) => {
      if (lastPage.offset + lastPage.limit >= lastPage.count) return undefined;

      return { offset: lastPage.offset + opts.pageSize };
    },
    getPreviousPageParam: (firstPage: any) => {
      const prevPage = firstPage.offset - opts.pageSize;
      return prevPage <= 0 ? undefined : { offset: prevPage };
    },
  });
};
